import { useEffect } from "react";
import { Link, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, ArrowLeft, Calendar } from "lucide-react";

const CATEGORY_COLORS: Record<string, string> = {
  "inside-out-protocol": "bg-[#c4956a]/10 text-[#c4956a] border-[#c4956a]/30",
  "single-tech": "bg-[#4a7c6f]/10 text-[#4a7c6f] border-[#4a7c6f]/30",
  "dallas-market": "bg-[#8b7355]/10 text-[#8b7355] border-[#8b7355]/30",
};

const RELATED_READING: Record<string, Array<{ href: string; label: string }>> = {
  "inside-out-protocol": [
    { href: "/face-skin-rejuvenation", label: "Face & Skin Rejuvenation" },
    { href: "/skin-analysis", label: "In-Person Skin Analysis" },
    { href: "/gallery", label: "Explore Treatment Information" },
  ],
  "single-tech": [
    { href: "/rf-microneedling", label: "RF Microneedling" },
    { href: "/botox", label: "Botox & Neuromodulators" },
    { href: "/ai-skin-analysis", label: "AI Skin Analysis" },
  ],
  "dallas-market": [
    { href: "/med-spa-near-me", label: "Physician-Led Med Spa in Dallas" },
    { href: "/areas-we-serve", label: "Areas We Serve" },
    { href: "/why-choose-us", label: "Why Choose Lakewood Med Spa" },
  ],
};

const DEFAULT_RELATED_READING = [
  { href: "/face-skin-rejuvenation", label: "Face & Skin Rejuvenation" },
  { href: "/packages", label: "Treatment Packages" },
  { href: "/financing", label: "Financing & Payment Plans" },
];

function formatDate(date: Date | string | null) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = trpc.blog.getBySlug.useQuery(
    { slug: slug ?? "" },
    { enabled: !!slug }
  );

  const { data: allPosts } = trpc.blog.getAll.useQuery();

  // Update page title, meta, and inject BlogPosting schema for SEO
  useEffect(() => {
    if (post) {
      document.title = post.seoTitle ?? post.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && post.seoDescription) {
        metaDesc.setAttribute("content", post.seoDescription);
      }

      // Inject BlogPosting + Person (author) JSON-LD schema
      const existingSchema = document.querySelector('script[data-blog-schema]');
      if (existingSchema) existingSchema.remove();

      const blogSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.seoTitle ?? post.title,
        "description": post.seoDescription ?? post.excerpt ?? "",
        "url": `https://lakewoodmedspa.com/blog/${post.slug}`,
        "datePublished": post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined,
        "dateModified": post.updatedAt ? new Date(post.updatedAt).toISOString() : undefined,
        "author": {
          "@type": "Person",
          "@id": "https://lakewoodmedspa.com/#dr-rajan-kohli",
          "name": "Dr. Rajan Kohli MD",
          "honorificPrefix": "Dr.",
          "honorificSuffix": "MD",
          "jobTitle": "Medical Director & Founder",
          "url": "https://lakewoodmedspa.com/about",
          "image": "https://d2xsxph8kpxj0f.cloudfront.net/310519663381010537/AX6sNuigosh2pncBHVxRxQ/dr-kohli-headshot-v2_47f7d6ce.jpg",
          "worksFor": {
            "@type": "MedicalBusiness",
            "name": "Lakewood Med Spa",
            "url": "https://lakewoodmedspa.com"
          }
        },
        "publisher": {
          "@type": "MedicalBusiness",
          "@id": "https://lakewoodmedspa.com/#organization",
          "name": "Lakewood Med Spa",
          "logo": {
            "@type": "ImageObject",
            "url": "https://d2xsxph8kpxj0f.cloudfront.net/310519663381010537/AX6sNuigosh2pncBHVxRxQ/logo_optimized_1ee89f17.webp"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://lakewoodmedspa.com/blog/${post.slug}`
        },
        "isPartOf": {
          "@type": "Blog",
          "name": "Lakewood Med Spa Journal",
          "url": "https://lakewoodmedspa.com/blog"
        }
      };

      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-blog-schema", "true");
      script.textContent = JSON.stringify(blogSchema);
      document.head.appendChild(script);
    }
    return () => {
      document.title = "Lakewood Med Spa — Dallas";
      document.querySelector('script[data-blog-schema]')?.remove();
    };
  }, [post]);

  // Related posts: same category, different slug, max 3
  const related = (allPosts ?? [])
    .filter((p) => p.slug !== slug && p.category === post?.category)
    .slice(0, 3);
  const relatedReading = RELATED_READING[post?.category ?? ""] ?? DEFAULT_RELATED_READING;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5]">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <Skeleton className="h-4 w-32 mb-8" />
          <Skeleton className="h-8 w-24 mb-4" />
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-12 w-4/5 mb-8" />
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif text-[#1a1a18] mb-4">Post Not Found</h1>
          <p className="text-[#6b6b5f] mb-6">This post may have been moved or unpublished.</p>
          <Link href="/blog">
            <button className="bg-[#c4956a] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#b8845a] transition-colors">
              Back to Journal
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Top nav */}
      <div className="bg-white border-b border-[#e8e0d5] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link href="/blog">
            <button className="flex items-center gap-2 text-[#6b6b5f] hover:text-[#c4956a] transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" />
              The Lakewood Journal
            </button>
          </Link>
          <Link href="/">
            <span className="text-[#1a1a18] font-serif text-sm font-medium hover:text-[#c4956a] transition-colors">
              Lakewood Med Spa
            </span>
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 py-12">
        {/* Meta */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <span
            className={`text-xs font-semibold px-3 py-1 rounded-full border ${
              CATEGORY_COLORS[post.category] ?? "bg-gray-100 text-gray-600"
            }`}
          >
            {post.categoryLabel}
          </span>
          <span className="text-[#9b9b8f] text-sm flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {post.readTime}
          </span>
          {post.publishedAt && (
            <span className="text-[#9b9b8f] text-sm flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(post.publishedAt)}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-serif font-light text-[#1a1a18] leading-tight mb-6">
          {post.title}
        </h1>

        {/* Excerpt / Lead */}
        <p className="text-lg text-[#6b6b5f] leading-relaxed mb-8 border-l-2 border-[#c4956a] pl-5 italic">
          {post.excerpt}
        </p>

        {/* Author */}
        <div className="flex items-center gap-3 mb-10 pb-10 border-b border-[#e8e0d5]">
          <div className="w-10 h-10 rounded-full bg-[#c4956a]/15 flex items-center justify-center">
            <span className="text-[#c4956a] text-sm font-semibold">LM</span>
          </div>
          <div>
            <p className="text-[#1a1a18] text-sm font-medium">{post.author}</p>
            <p className="text-[#9b9b8f] text-xs">Lakewood Med Spa, Dallas TX</p>
          </div>
        </div>

        {/* Content */}
        <div
          className="blog-content prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <aside className="mt-12 border-y border-[#e8e0d5] py-7" aria-label="Related reading">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase text-[#c4956a] mb-3">Continue exploring</p>
          <h2 className="text-xl font-serif font-light text-[#1a1a18] mb-4">Related reading</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-3">
            {relatedReading.map((item) => (
              <Link key={item.href} href={item.href} className="text-sm font-medium text-[#4a7c6f] hover:text-[#c4956a] underline underline-offset-4">
                {item.label}
              </Link>
            ))}
          </div>
        </aside>

        {/* CTA */}
        <div className="mt-16 bg-[#1a1a18] rounded-2xl p-8 text-center">
          <p className="text-[#c4956a] text-xs tracking-widest uppercase font-medium mb-3">
            Ready to Start?
          </p>
          <h3 className="text-2xl font-serif font-light text-white mb-3">
            Schedule Your Physician Consultation
          </h3>
          <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-md mx-auto">
            Every treatment plan begins with a comprehensive physician assessment.
            No packages, no pressure — just an honest evaluation of what will work
            for your anatomy and goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a href="https://medsparecord.com/book" className="bg-[#c4956a] text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-[#b8845a] transition-colors inline-block">
              Book a Consultation
            </a>
            <a href="tel:+12142476793" className="text-white/70 text-sm font-medium hover:text-white transition-colors">
              Call / Text (214) 247-6793
            </a>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {related.length > 0 && (
        <div className="border-t border-[#e8e0d5] bg-white py-12 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-serif font-light text-[#1a1a18] mb-6">
              Related Articles
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((p) => (
                <Link key={p.id} href={`/blog/${p.slug}`}>
                  <div className="group cursor-pointer">
                    <span
                      className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border inline-block mb-2 ${
                        CATEGORY_COLORS[p.category] ?? "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {p.categoryLabel}
                    </span>
                    <h3 className="text-sm font-medium text-[#1a1a18] leading-snug group-hover:text-[#c4956a] transition-colors line-clamp-3">
                      {p.title}
                    </h3>
                    <p className="text-[#9b9b8f] text-xs mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {p.readTime}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
